/**
 * MORVIX AI — transport layer.
 *
 * The consultant answers locally by default (see ./engine.ts). To connect a
 * real model, expose a SERVER-SIDE endpoint that holds the API key and set
 * `VITE_MORVIX_AI_ENDPOINT` to its URL. The browser only ever talks to your
 * own backend — no provider keys or credentials live in this bundle.
 *
 * Expected backend contract:
 *   POST <endpoint>
 *   body: { message, history: [{ role, text }], session, knowledgeVersion }
 *   200 : { reply: string, actions?: [{ label, kind, value }], session?: {...} }
 *
 * Any error, timeout or malformed payload falls back to the local engine, so
 * the consultant is never broken for a visitor.
 */

import { respond } from './engine'
import type { ChatMessage, ConsultantSession, EngineResult } from './types'

const ENDPOINT: string =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_MORVIX_AI_ENDPOINT) || ''

/** Bump when the knowledge base changes so a backend can invalidate caches. */
export const KNOWLEDGE_VERSION = '2026.07'

export const hasRemoteBrain = Boolean(ENDPOINT)

const TIMEOUT_MS = 12000

export async function askConsultant(
  message: string,
  history: ChatMessage[],
  session: ConsultantSession,
): Promise<EngineResult> {
  const local = respond(message, session)

  // Mid-flow (discovery / qualification) the deterministic engine stays in
  // control so structured answers are never lost to a model hallucination.
  if (!hasRemoteBrain || session.flow !== 'idle') return local

  try {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS)
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
      body: JSON.stringify({
        message,
        history: history.slice(-12).map((item) => ({ role: item.role, text: item.text })),
        session,
        knowledgeVersion: KNOWLEDGE_VERSION,
      }),
    })
    clearTimeout(timer)
    if (!response.ok) return local

    const data = (await response.json()) as {
      reply?: string
      actions?: EngineResult['replies'][number]['actions']
      session?: Partial<ConsultantSession>
    }
    if (!data?.reply) return local

    return {
      replies: [{ text: data.reply, actions: data.actions ?? local.replies[0]?.actions }],
      session: { ...local.session, ...(data.session ?? {}) },
    }
  } catch {
    return local
  }
}
